<script lang="ts">
    import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import PlaceOrderBoostCampaignIcon from "@apps/rewards/components/Icons/EarningRulesIcons/PlaceOrderBoostCampaignIcon.svelte";
    import RewardsCustomerStatusBasedContent from "@apps/rewards/components/RewardsCustomerStatusBasedContent.svelte";
    import RewardsDynamicCustomerIcon from "@apps/rewards/components/RewardsDynamicCustomerIcon.svelte";
    import RewardsEarningRulesReward from "@apps/rewards/components/RewardsEarningRules/RewardsEarningRulesReward.svelte";
    import RewardsList from "@apps/rewards/components/RewardsList/RewardsList.svelte";
    import RewardsGeneralTheme from "@apps/rewards/components/Theme/RewardsGeneralTheme.svelte";
    import {
        DATA_GW_SELECTORS,
        ICON_SIZES,
    } from "@apps/rewards/constants/constants";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type RewardsBoostCampaignQuery from "@apps/rewards/modules/BoostCampaign/query/RewardsBoostCampaignQuery";
    import type RewardsBoostCampaignService from "@apps/rewards/modules/BoostCampaign/service/RewardsBoostCampaignService";
    import { REWARDS_EARNING_RULE_TYPES } from "@apps/rewards/modules/EarningRules/constants";
    import { REWARDS_EARNING_RULE_ICONS } from "@apps/rewards/modules/EarningRules/icons";
    import type RewardsEarningRulesService from "@apps/rewards/modules/EarningRules/service/RewardsEarningRulesService";
    import type RewardsProgramAccountQuery from "@apps/rewards/modules/ProgramAccount/query/RewardsProgramAccountQuery";
    import type RewardsProgramAccountService from "@apps/rewards/modules/ProgramAccount/service/RewardsProgramAccountService";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import { fetchAndOpenModal } from "@apps/rewards/utils/modal/utils";
    import { normalizeLocale } from "@apps/rewards/utils/normalizeLocale/normalizeLocale";
    import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
    import type { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import type { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
    import { filter, map, take, tap } from "rxjs";
    import { onMount } from "svelte";
    import { fade } from "svelte/transition";

    import type { ModalManager } from "@components/Modal/ModalManager";
    import PreviewBadge from "@components/PreviewBadge.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardPageWidgetAuthButtons from "../../components/RewardPageWidgetAuthButtons.svelte";
    import RewardsPageActionCompleted from "../../components/RewardsPageActionCompleted.svelte";
    import RewardsPageCustomerBalance from "../../components/RewardsPageCustomerBalance.svelte";
    import RewardsPageWidgetCard from "../../components/RewardsPageWidgetCard.svelte";
    import RewardsPageWidgetCardLayoutSkeleton from "../../components/RewardsPageWidgetCardLayoutSkeleton.svelte";
    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetEmptyState from "../../components/RewardsPageWidgetEmptyState.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";

    //Props
    export let programAccountService: RewardsProgramAccountService;
    export let earningRulesService: RewardsEarningRulesService;
    export let boostCampaignService: RewardsBoostCampaignService;
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let boostCampaignQuery: RewardsBoostCampaignQuery;
    export let programAccountQuery: RewardsProgramAccountQuery;
    export let i18n: GwI18n;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let amountFormatter: RewardsAmountFormatter;
    export let dynamicImportModule: DynamicImportModule;
    export let modalManager: ModalManager;
    export let urlsBuilder: URLsBuilder;
    export let sanitizer: HTMLSanitizerInterface;
    export let locationController: LocationControllerInterface;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let localizationInfoProvider: LocalizationInfoProviderInterface;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;
    export let showPointsBalance: boolean | null;
    export let isDesignMode: boolean | null;
    export let afterWidgetInject: () => void;

    //Variables
    const isInitialLoading$ = earningRulesService.isInitialLoading();
    const sortedRulesList$ = earningRulesService.getSortedRulesList();
    const boostCampaign$ = boostCampaignQuery.boostCampaign$;
    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    const points$ = programAccountQuery.points$;
    const branding$ = storefrontValuesService.selectRewardsBranding();

    //Component lifecycle
    onMount(() => {
        earningRulesService.checkRequestStatusAndGetEarningRules();
        boostCampaignService.checkRequestStatusAndGetActiveBoostCampaign();

        const isCustomerSubscription = isCustomer$
            .pipe(
                filter((isCustomer) => isCustomer),
                take(1),
                tap(() =>
                    programAccountService.startIntervalRequests({
                        locale: normalizeLocale(
                            localizationInfoProvider.getLanguageIsoCode()
                        ),
                    })
                )
            )
            .subscribe();

        afterWidgetInject();

        return () => {
            isCustomerSubscription.unsubscribe();

            if ($isCustomer$) programAccountService.stopIntervalRequests();
        };
    });
</script>

{#if $branding$}
    <RewardsGeneralTheme rewardsBrandingGeneral={$branding$.general}>
        <div class="gw-rd-ways-to-earn-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    {#if isDesignMode}
                        <PreviewBadge />
                    {/if}
                    {#if $isInitialLoading$}
                        <RewardsPageWidgetCardLayoutSkeleton />
                    {:else}
                        <RewardsPageWidgetTitle>
                            <T {namespace} translationKey="widget_title" />
                        </RewardsPageWidgetTitle>
                        <RewardsCustomerStatusBasedContent
                            isCustomerContent={$isCustomer$ &&
                                !!showPointsBalance}
                        >
                            <RewardsPageCustomerBalance
                                slot="customer-content"
                                customerBalance={$points$?.balance}
                                pointsExpiration={$points$?.expiresAt}
                                {amountFormatter}
                                {i18n}
                                {namespace}
                                {pointsNamespace}
                            />
                        </RewardsCustomerStatusBasedContent>
                        <div
                            class="gw-rd-ways-to-earn-page-widget__list"
                            in:fade={{ duration: 300 }}
                        >
                            <RewardsList
                                items={$sortedRulesList$}
                                layout="page"
                                let:item
                            >
                                {@const leaveReviewExtraPoints =
                                    item.type ===
                                    REWARDS_EARNING_RULE_TYPES.leaveReview
                                        ? item.extraPointsForPhoto
                                        : null}
                                {@const isBoostCampaign =
                                    $boostCampaign$ &&
                                    item.type ===
                                        REWARDS_EARNING_RULE_TYPES.placeOrder}

                                <RewardsPageWidgetCard
                                    isCompleted={item.completed}
                                    isCustomer={$isCustomer$}
                                    dataGwSelector={`${DATA_GW_SELECTORS.waysToEarnPageWidget}-${item.id}`}
                                    canOpenModal
                                    on:click={() =>
                                        $isCustomer$ &&
                                        fetchAndOpenModal({
                                            modalName:
                                                REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_EARN_PAGE_WIDGET_MODAL,
                                            modalManager,
                                            dynamicImportManager:
                                                dynamicImportModule.exports
                                                    .dynamicImportManager,
                                            params: {
                                                ruleId: item.id,
                                                closeAfterSubmit: true,
                                            },
                                        })}
                                >
                                    <RewardsDynamicCustomerIcon
                                        slot="icon"
                                        viewBox={item.type ===
                                        REWARDS_EARNING_RULE_TYPES.followOnCustom
                                            ? "0 0 20 20"
                                            : "0 0 32 32"}
                                        iconSize="large"
                                        customerIcon={getImageSizeUrl(
                                            item.icon?.sizes,
                                            ICON_SIZES.small
                                        ) || item.icon?.url}
                                        defaultIcon={isBoostCampaign
                                            ? PlaceOrderBoostCampaignIcon
                                            : REWARDS_EARNING_RULE_ICONS[
                                                  item.type
                                              ]}
                                    />
                                    <Text
                                        slot="title"
                                        component="h2"
                                        variant="base"
                                        weight="semibold"
                                    >
                                        {item.title}
                                    </Text>
                                    <Text
                                        slot="reward"
                                        component="span"
                                        variant="secondary"
                                        weight="normal"
                                        compact
                                    >
                                        <RewardsEarningRulesReward
                                            reward={item.rewarding}
                                            boostCampaignMultiplier={$boostCampaign$?.pointsMultiplier}
                                            {leaveReviewExtraPoints}
                                            {currencyFormatter}
                                            {amountFormatter}
                                            {urlsBuilder}
                                            {sanitizer}
                                            {i18n}
                                            {namespace}
                                            {pointsNamespace}
                                        />
                                    </Text>
                                    <RewardPageWidgetAuthButtons
                                        slot="auth-buttons"
                                        {locationController}
                                        {namespace}
                                        {urlsBuilder}
                                    />
                                    <RewardsPageActionCompleted
                                        slot="completed"
                                        {namespace}
                                    />
                                </RewardsPageWidgetCard>
                                <RewardsPageWidgetEmptyState slot="empty-state">
                                    <Text slot="content">
                                        <T
                                            {namespace}
                                            translationKey="earning_rules_empty_state"
                                        />
                                    </Text>
                                </RewardsPageWidgetEmptyState>
                            </RewardsList>
                        </div>
                    {/if}
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsGeneralTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../../themes/globals.css";

    .gw-rd-ways-to-earn-page-widget {
        --gw-image-skeleton-backdrop-color: #e2e8f0;

        background-color: var(--gw-white-bg);

        &__customer-balance {
            display: flex;
            align-items: center;
        }

        &__list {
            width: 100%;
        }
    }
</style>
